import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CloseCircleGreyIcon } from "assets";
import { ModalPortal, MODAL_LAYER } from "components/modal-portal";
import { CreditSlider } from "components/credit-slider";
import { CreditAmountField } from "components/credit-slider/credit-amount-field";
import { useCheckout } from "features/checkout";
import type { CheckoutTopupData } from "features/checkout";
import { useCreditPricing } from "hooks/useCreditPricing";
import {
  buildCreditSalesContactUrl,
  CREDIT_PURCHASE_STEP,
  MIN_CREDIT_PURCHASE,
  cvsForCredits,
  formatCurrency,
  jobsForCredits,
} from "utils/pricing";

interface BuyAdditionalCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentBalance?: number;
  onPaymentSuccess?: () => void;
}

const BuyAdditionalCreditModal = ({
  isOpen,
  onClose,
  currentBalance = 0,
  onPaymentSuccess,
}: BuyAdditionalCreditModalProps) => {
  const [credits, setCredits] = useState(MIN_CREDIT_PURCHASE);
  const [draftCredits, setDraftCredits] = useState<number | null>(null);
  const navigate = useNavigate();
  const { openCheckout } = useCheckout();
  const { pricing, priceFor } = useCreditPricing();

  const totalPrice = priceFor(credits);
  const isSalesRequest = draftCredits !== null && draftCredits > pricing.maxPurchase;

  const handleTopup = () => {
    if (isSalesRequest) {
      navigate(buildCreditSalesContactUrl(draftCredits ?? undefined));
      onClose();
      return;
    }

    const topupData: CheckoutTopupData = {
      credits,
      totalPrice,
      costPerCredit: pricing.pricePerCreditNgn,
      validityDays: pricing.validityDays,
      currency: "NGN",
    };
    openCheckout(topupData, onPaymentSuccess);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: MODAL_LAYER.gate }}>
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-[#F9FAFB] rounded-2xl max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 z-10 hover:opacity-80 transition-opacity"
          >
            <CloseCircleGreyIcon className="w-8 h-8" />
          </button>

          <div className="p-6 pt-10">
            {/* Header */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-[#012169] mb-2">Buy Additional Credit</h2>
              <p className="text-[#667085] text-sm">
                Top up your credits and pick up your experience right where you left off.
              </p>
            </div>

            {/* Current Balance Badge */}
            <div className="flex justify-center mb-6">
              <span className="px-4 py-2 bg-[#EFF8FF] rounded-lg text-sm text-[#475467]">
                Current Balance:{" "}
                <span className="font-semibold text-[#344054]">
                  {currentBalance.toLocaleString()} credits
                </span>
              </span>
            </div>

            {/* Pricing Card */}
            <div className="border border-[#EAECF0] rounded-xl sm:p-6 py-5 px-2 mb-6">
              {/* Price Header */}
              <div className="flex justify-between sm:items-start items-center mb-6">
                <div className="text-3xl sm:text-4xl font-bold text-[#012169]">
                  {formatCurrency(totalPrice, "NGN")}
                </div>
                <div className="sm:text-xl text-base sm:font-bold font-medium text-[#012169]">
                  Credit Pricing
                </div>
              </div>

              {/* Credit amount — typed or dragged */}
              <div className="mb-6">
                <CreditAmountField
                  id="topup-credit-amount"
                  value={credits}
                  onChange={setCredits}
                  onDraftChange={setDraftCredits}
                />
              </div>

              {/* Credit amount */}
              <div className="mb-8">
                <CreditSlider
                  min={pricing.minPurchase}
                  max={pricing.maxPurchase}
                  step={CREDIT_PURCHASE_STEP}
                  value={credits}
                  onChange={setCredits}
                  label="Credits to purchase"
                  trackHeight={20}
                  handleSize={32}
                />
              </div>

              {/* Info Cards */}
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="bg-[#EFF8FF] rounded-lg px-4 py-3">
                  <p className="text-xs text-[#1D2939] mb-1 font-medium">Cost per credit</p>
                  <p className="text-lg font-bold text-[#012169]">
                    {formatCurrency(pricing.pricePerCreditNgn, "NGN")}
                  </p>
                </div>
                <div className="bg-[#EFF8FF] rounded-lg px-4 py-3">
                  <p className="text-xs text-[#1D2939] mb-1 font-medium">Create up to</p>
                  <p className="text-lg font-bold text-[#012169]">
                    {jobsForCredits(credits)}{" "}
                    <span className="text-sm font-normal text-[#344054]">Jobs</span>
                  </p>
                </div>
                <div className="bg-[#EFF8FF] rounded-lg px-4 py-3">
                  <p className="text-xs text-[#1D2939] mb-1 font-medium">Screen up to</p>
                  <p className="text-lg font-bold text-[#012169]">
                    {cvsForCredits(credits).toLocaleString()}{" "}
                    <span className="text-sm font-normal text-[#344054]">CVs</span>
                  </p>
                </div>
              </div>
            </div>

            {isSalesRequest && (
              <div className="mb-4 rounded-xl border border-[#B2CCFF] bg-[#EFF8FF] px-4 py-3 text-sm text-[#344054]">
                Need more than {pricing.maxPurchase.toLocaleString()} credits? We&apos;ll route you to sales.
              </div>
            )}

            <p className="text-center text-xs text-[#667085] mb-4">
              Purchased credits stay valid for {pricing.validityDays} days.
            </p>

            {/* Topup Button */}
            <button
              onClick={handleTopup}
              className="w-full py-4 bg-[#012169] text-white font-medium rounded-lg hover:bg-[#011557] transition-colors"
            >
              {isSalesRequest ? "Talk to sales" : `Topup ${credits.toLocaleString()} credits`}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export { BuyAdditionalCreditModal };
